import React, { useState } from 'react'
import {
  Card,
  Button,
  Input,
  Space,
  Typography,
  message,
  Row,
  Col,
  Divider,
} from 'antd'
import {
  DownloadOutlined,
  EyeOutlined,
  SettingOutlined,
} from '@ant-design/icons'
import { downloadCertificateWithPosition } from '../../../utils/helpers'
import {
  previewCertificate,
  getCertificateAsBase64,
  CertificateConfig,
} from '../../../utils/certificateGenerator'

const { Title, Text, Paragraph } = Typography

const CertificateDemo: React.FC = () => {
  const [studentName, setStudentName] = useState('John Doe')
  const [courseName, setCourseName] = useState('React Development Fundamentals')
  const [position, setPosition] = useState({ x: undefined, y: 400 }) // X auto-centered
  const [coursePosition, setCoursePosition] = useState({ x: undefined, y: 320 }) // Course X auto-centered
  const [fontSize, setFontSize] = useState(24)
  const [courseFontSize, setCourseFontSize] = useState(18)
  const [previewUrl, setPreviewUrl] = useState('')
  const [loading, setLoading] = useState(false)

  const handleDownload = async () => {
    try {
      setLoading(true)
      const config: CertificateConfig = {
        name: studentName,
        x: position.x, // Will be undefined for auto-centering
        y: position.y,
        courseName,
        courseX: coursePosition.x, // Will be undefined for auto-centering
        courseY: coursePosition.y,
        fontSize,
        fontColor: { r: 42, g: 170, b: 250 },
        courseFontSize,
        courseFontColor: { r: 51, g: 51, b: 51 },
      }

      await downloadCertificateWithPosition(config)
      message.success('Sertifikat berhasil diunduh!')
    } catch (error) {
      message.error('Gagal mengunduh sertifikat')
      console.error('Download error:', error)
    } finally {
      setLoading(false)
    }
  }

  const handlePreview = async () => {
    try {
      setLoading(true)
      const config: CertificateConfig = {
        name: studentName,
        x: position.x, // Will be undefined for auto-centering
        y: position.y,
        courseName,
        courseX: coursePosition.x, // Will be undefined for auto-centering
        courseY: coursePosition.y,
        fontSize,
        fontColor: { r: 42, g: 170, b: 250 },
        courseFontSize,
        courseFontColor: { r: 51, g: 51, b: 51 },
      }

      await previewCertificate(config)
      message.success('Preview dibuka di tab baru!')
    } catch (error) {
      message.error('Gagal membuka preview')
      console.error('Preview error:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleGeneratePreview = async () => {
    try {
      setLoading(true)
      const config: CertificateConfig = {
        name: studentName,
        x: position.x, // Will be undefined for auto-centering
        y: position.y,
        courseName,
        courseX: coursePosition.x, // Will be undefined for auto-centering
        courseY: coursePosition.y,
        fontSize,
        fontColor: { r: 42, g: 170, b: 250 },
        courseFontSize,
        courseFontColor: { r: 51, g: 51, b: 51 },
      }

      const base64Url = await getCertificateAsBase64(config)
      setPreviewUrl(base64Url)
      message.success('Preview berhasil di-generate!')
    } catch (error) {
      message.error('Gagal generate preview')
      console.error('Generate preview error:', error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{ padding: '20px' }}>
      <Title level={2}>Demo Sistem Sertifikat</Title>

      <Row gutter={[24, 24]}>
        <Col xs={24} lg={12}>
          <Card title="Konfigurasi Sertifikat">
            <Space direction="vertical" style={{ width: '100%' }}>
              <div>
                <Text strong>Nama Siswa:</Text>
                <Input
                  value={studentName}
                  onChange={(e) => setStudentName(e.target.value)}
                  placeholder="Masukkan nama siswa"
                  style={{ marginTop: 8 }}
                />
              </div>

              <div>
                <Text strong>Nama Course:</Text>
                <Input
                  value={courseName}
                  onChange={(e) => setCourseName(e.target.value)}
                  placeholder="Masukkan nama course"
                  style={{ marginTop: 8 }}
                />
              </div>

              <div>
                <Text strong>
                  Posisi X:{' '}
                  {position.x !== undefined
                    ? `${position.x}pt`
                    : 'Auto (Tengah)'}
                </Text>
                <input
                  type="range"
                  min="0"
                  max="595"
                  value={position.x || 297.5}
                  onChange={(e) =>
                    setPosition((prev) => ({
                      ...prev,
                      x: parseInt(e.target.value),
                    }))
                  }
                  style={{ width: '100%', marginTop: 8 }}
                />
                <Text type="secondary" style={{ fontSize: 12, marginTop: 4 }}>
                  Kosongkan untuk auto-centering berdasarkan panjang nama
                </Text>
              </div>

              <div>
                <Text strong>Posisi Y: {position.y}pt</Text>
                <input
                  type="range"
                  min="0"
                  max="842"
                  value={position.y}
                  onChange={(e) =>
                    setPosition((prev) => ({
                      ...prev,
                      y: parseInt(e.target.value),
                    }))
                  }
                  style={{ width: '100%', marginTop: 8 }}
                />
              </div>

              <div>
                <Text strong>Ukuran Font: {fontSize}pt</Text>
                <input
                  type="range"
                  min="8"
                  max="72"
                  value={fontSize}
                  onChange={(e) => setFontSize(parseInt(e.target.value))}
                  style={{ width: '100%', marginTop: 8 }}
                />
              </div>

              <Divider>Pengaturan Course</Divider>

              <div>
                <Text strong>
                  Posisi X Course:{' '}
                  {coursePosition.x !== undefined
                    ? `${coursePosition.x}pt`
                    : 'Auto (Tengah)'}
                </Text>
                <input
                  type="range"
                  min="0"
                  max="595"
                  value={coursePosition.x || 297.5}
                  onChange={(e) =>
                    setCoursePosition((prev) => ({
                      ...prev,
                      x: parseInt(e.target.value),
                    }))
                  }
                  style={{ width: '100%', marginTop: 8 }}
                />
                <Text type="secondary" style={{ fontSize: 12, marginTop: 4 }}>
                  Kosongkan untuk auto-centering berdasarkan panjang course
                </Text>
              </div>

              <div>
                <Text strong>Posisi Y Course: {coursePosition.y}pt</Text>
                <input
                  type="range"
                  min="0"
                  max="842"
                  value={coursePosition.y}
                  onChange={(e) =>
                    setCoursePosition((prev) => ({
                      ...prev,
                      y: parseInt(e.target.value),
                    }))
                  }
                  style={{ width: '100%', marginTop: 8 }}
                />
              </div>

              <div>
                <Text strong>Ukuran Font Course: {courseFontSize}pt</Text>
                <input
                  type="range"
                  min="8"
                  max="72"
                  value={courseFontSize}
                  onChange={(e) => setCourseFontSize(parseInt(e.target.value))}
                  style={{ width: '100%', marginTop: 8 }}
                />
              </div>

              <Divider />

              <Space>
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={handleDownload}
                  loading={loading}
                >
                  Download Sertifikat
                </Button>
                <Button
                  icon={<EyeOutlined />}
                  onClick={handlePreview}
                  loading={loading}
                >
                  Preview di Tab Baru
                </Button>
                <Button
                  icon={<SettingOutlined />}
                  onClick={handleGeneratePreview}
                  loading={loading}
                >
                  Generate Preview
                </Button>
              </Space>
            </Space>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="Preview Sertifikat">
            {previewUrl ? (
              <iframe
                src={previewUrl}
                style={{
                  width: '100%',
                  height: '400px',
                  border: '1px solid #d9d9d9',
                  borderRadius: '8px',
                }}
                title="Certificate Preview"
              />
            ) : (
              <div
                style={{
                  height: '400px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#f5f5f5',
                  border: '1px solid #d9d9d9',
                  borderRadius: '8px',
                }}
              >
                <Text type="secondary">
                  Klik "Generate Preview" untuk melihat sertifikat
                </Text>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      <Divider />

      <Card title="Cara Penggunaan">
        <Paragraph>
          <Text strong>Fitur yang tersedia:</Text>
        </Paragraph>
        <ul>
          <li>
            <Text strong>Pengaturan Posisi:</Text> Atur posisi nama menggunakan
            slider X dan Y
          </li>
          <li>
            <Text strong>Ukuran Font:</Text> Sesuaikan ukuran font dari 8pt
            hingga 72pt
          </li>
          <li>
            <Text strong>Download:</Text> Unduh sertifikat dengan nama dan
            posisi yang sudah diatur
          </li>
          <li>
            <Text strong>Preview:</Text> Lihat hasil sertifikat sebelum diunduh
          </li>
        </ul>

        <Paragraph>
          <Text strong>Koordinat PDF:</Text>
        </Paragraph>
        <ul>
          <li>X: 0-595pt (lebar A4)</li>
          <li>Y: 0-842pt (tinggi A4)</li>
          <li>Auto-center: X otomatis di tengah berdasarkan panjang nama</li>
          <li>Posisi manual: Y=400 (tengah halaman)</li>
        </ul>

        <Paragraph>
          <Text strong>Tips:</Text>
        </Paragraph>
        <ul>
          <li>Gunakan preview untuk memastikan posisi nama sudah tepat</li>
          <li>Koordinat (0,0) berada di pojok kiri bawah halaman</li>
          <li>Koordinat (595,842) berada di pojok kanan atas halaman</li>
        </ul>
      </Card>
    </div>
  )
}

export default CertificateDemo
